/**
 * @file 校区新建 / 编辑弹窗
 * @description Dialog 表单：名称、主账号、密码、点数；提交调用真实接口。
 *
 * @whenToUse 校区管理页创建与编辑。
 * @whenNotToUse 其他业务表单弹窗。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import type { CampusItem } from '@/api/campus.ts'
import {
  EMPTY_CAMPUS_FORM,
  type CampusFormValues,
} from '@/components/pages/campus-page/types.ts'

/** 主账号：纯字母 / 纯数字 / 字母+数字，可含下划线 */
const ACCOUNT_PATTERN = /^[a-zA-Z0-9_]+$/

/**
 * 过滤主账号非法字符（仅保留字母、数字、下划线）
 * @param value 原始输入
 */
function sanitizeAccount(value: string): string {
  return value.replace(/[^a-zA-Z0-9_]/g, '')
}

export type CampusFormDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: CampusItem | null
  /** 提交（编辑目标由 editing 提供，无需 id） */
  onSubmit: (payload: {
    campusName: string
    userName: string
    points: number
    password?: string
  }) => Promise<void>
}

/**
 * 校区创建 / 编辑 Dialog
 */
export function CampusFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: CampusFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<CampusFormValues>(EMPTY_CAMPUS_FORM)
  const [errors, setErrors] = useState<Partial<Record<keyof CampusFormValues, string>>>(
    {},
  )
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        campusName: editing.campusName,
        userName: editing.userName,
        password: '',
        points: editing.points,
      })
    } else {
      setValues(EMPTY_CAMPUS_FORM)
    }
    setErrors({})
  }, [open, editing])

  const setField = <K extends keyof CampusFormValues>(
    key: K,
    value: CampusFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const validate = () => {
    const next: Partial<Record<keyof CampusFormValues, string>> = {}
    if (!values.campusName.trim()) next.campusName = '请输入校区名称'
    const account = values.userName.trim()
    if (!account) next.userName = '请输入主账号'
    else if (!ACCOUNT_PATTERN.test(account))
      next.userName = '仅支持字母、数字和下划线'
    if (!isEdit && values.password.trim() && values.password.length < 6)
      next.password = '密码至少 6 位'
    if (values.points < 0) next.points = '点数不能为负'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate() || submitting) return
    setSubmitting(true)
    try {
      await onSubmit({
        campusName: values.campusName.trim(),
        userName: values.userName.trim(),
        points: values.points,
        password: values.password.trim() || undefined,
      })
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑校区' : '创建校区'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button loading={submitting} onClick={handleSubmit}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="校区名称" required error={errors.campusName}>
          <Input
            value={values.campusName}
            invalid={Boolean(errors.campusName)}
            placeholder="请输入校区名称"
            onChange={(event) => setField('campusName', event.target.value)}
          />
        </FormField>
        <FormField
          label="校区账号"
          required
          error={errors.userName}
          hint="仅支持字母、数字和下划线"
        >
          <Input
            value={values.userName}
            invalid={Boolean(errors.userName)}
            placeholder="如 campus_01"
            autoComplete="username"
            onChange={(event) =>
              setField('userName', sanitizeAccount(event.target.value))
            }
          />
        </FormField>
        {!isEdit ? (
          <FormField
            label="账号密码"
            error={errors.password}
            hint="留空默认 123456"
          >
            <Input
              type="password"
              value={values.password}
              invalid={Boolean(errors.password)}
              placeholder="请输入密码(默认123456)"
              autoComplete="new-password"
              onChange={(event) => setField('password', event.target.value)}
            />
          </FormField>
        ) : null}
        <FormField label="点数" required error={errors.points}>
          <InputNumber
            value={values.points}
            min={0}
            invalid={Boolean(errors.points)}
            onChange={(value) => setField('points', value ?? 0)}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
